import { useTranslation } from "@/hooks";
import { useUser } from "@/stores/auth";
import { useBookmarksContext } from "@/stores/bookmark";
import { useLanguages } from "@/stores/language";
import type { Dataset, Language } from "@/types/europafacts";
import type { BookmarkSet } from "@/types/europafacts/Bookmark";
import { logger } from "@/utils/debug";
import { formatBytes } from "@/utils/format";
import {
  Button,
  Card,
  Divider,
  Flex,
  Group,
  Image,
  Loader,
  Menu,
  Modal,
  Text,
  TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import {
  IconBookmark,
  IconBookmarkFilled,
  IconFolder,
  IconFolderHeart,
  IconPlus,
} from "@tabler/icons-react";
import { useMemo, useState } from "react";
import { Link } from "react-router";
import "./DatasetCard.css";

export function DatasetCard({ dataset }: { dataset: Dataset }) {
  const { currentLanguage } = useTranslation();
  const {
    isBookmarked,
    bookmarks,
    bookmarkSets,
    saveToMyBookmarks,
    removeFromMyBookmarks,
    saveToBookmarkSet,
    removeFromBookmarkSet,
    createBookmarkSet,
  } = useBookmarksContext();
  const languages = useLanguages();
  const user = useUser();
  const [loading, setLoading] = useState(false);
  const [createModalOpened, setCreateModalOpened] = useState(false);

  const isInMyBookmarks = bookmarks.some(
    (bookmark) => bookmark.db_table.db_table_id === dataset.db_table_id
  );

  const bookmarked = isBookmarked(dataset.db_table_id);

  const { t } = useTranslation();
  const createBookmarkSetForm = useForm({
    initialValues: {
      bookmark_set_name: "",
    },
    validate: {
      bookmark_set_name: (value) =>
        value.trim().length === 0 ? t("dataset").bookmarkSetNameRequired : null,
    },
  });

  async function onSaveToMyBookmarks() {
    setLoading(true);
    try {
      await saveToMyBookmarks(dataset.db_table_id);
    } catch (error) {
      logger.error("Failed to save to my bookmarks:", error);
    } finally {
      setLoading(false);
    }
  }

  async function onSaveToBookmarkSet(bookmarkSetId: string) {
    setLoading(true);
    try {
      await saveToBookmarkSet(bookmarkSetId, dataset.db_table_id);
    } catch (error) {
      logger.error("Failed to save to bookmark set:", error);
    } finally {
      setLoading(false);
    }
  }

  async function onRemoveFromMyBookmarks() {
    setLoading(true);
    try {
      await removeFromMyBookmarks(dataset.db_table_id);
    } catch (error) {
      logger.error("Failed to remove from my bookmarks:", error);
    } finally {
      setLoading(false);
    }
  }

  async function onRemoveFromBookmarkSet(bookmarkSetId: string) {
    setLoading(true);
    try {
      await removeFromBookmarkSet(bookmarkSetId, dataset.db_table_id);
    } catch (error) {
      logger.error("Failed to remove from bookmark set:", error);
    } finally {
      setLoading(false);
    }
  }

  function onCreateNewBookmarkSet() {
    setCreateModalOpened(true);
  }

  async function handleCreateBookmarkSet(values: {
    bookmark_set_name: string;
  }) {
    if (!user?.id) {
      logger.error("User not found");
      return;
    }

    setLoading(true);
    try {
      await createBookmarkSet(
        values.bookmark_set_name,
        user.id,
        dataset.db_table_id
      );
      createBookmarkSetForm.reset();
      setCreateModalOpened(false);
    } catch (error) {
      logger.error("Failed to create new bookmark set:", error);
    } finally {
      setLoading(false);
    }
  }

  function handleCloseCreateModal() {
    setCreateModalOpened(false);
    createBookmarkSetForm.reset();
  }

  const datasetName =
    dataset.table_name_translation?.[currentLanguage] ??
    dataset.table_name_original ??
    t("dataset").unknownDataset;
  const datasetDescription =
    dataset.description_long_translation?.[currentLanguage] ??
    dataset.description_long_original ??
    t("dataset").noDescriptionAvailable;

  const datasetFlagSrc = useMemo(() => {
    const language = languages.find(
      (lang: Language) => lang.iso2 === dataset.language_iso
    );
    return language?.flag_file;
  }, [languages, dataset.language_iso]);

  const datasetSource =
    (dataset.source_json_original?.data_source?.data_realm_name as string) ??
    t("dataset").unknownSource;
  const datasetRowCount = (dataset.row_count ?? 0).toLocaleString("da-DK", {
    style: "decimal",
    maximumFractionDigits: 0,
  });
  const datasetColumnCount = (dataset.column_count ?? 0).toLocaleString(
    "da-DK",
    {
      style: "decimal",
      maximumFractionDigits: 0,
    }
  );
  const datasetSize = useMemo(
    () => formatBytes(dataset.byte_count ?? 0),
    [dataset.byte_count]
  );
  const datasetDateCreated = dataset.datetime_created
    ? new Date(dataset.datetime_created).toLocaleDateString("da-DK", {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : t("dataset").unknownDate;

  const flagImageStyle = {
    objectFit: "cover" as const,
    width: 14,
    height: 10,
    borderRadius: "1px",
    filter: "drop-shadow(0px 1px 0px rgba(0, 0, 0, 0.15))",
  };

  const euFlagColor = "var(--mantine-color-eu_flag-filled)";
  const grayColor = "var(--mantine-color-gray-9)";

  const renderBookmarkButton = () => {
    if (loading) {
      return (
        <>
          <Loader size={16} color={euFlagColor} />
          <Text fz={15} fw={600} c={euFlagColor}>
            {bookmarked ? t("dataset").removing : t("dataset").saving}
          </Text>
        </>
      );
    }

    if (bookmarked) {
      return (
        <>
          <IconBookmarkFilled color={euFlagColor} size={16} />
          <Text fz={15} fw={600} c={euFlagColor}>
            {t("dataset").saved}
          </Text>
        </>
      );
    }

    return (
      <>
        <IconBookmark color={grayColor} size={16} />
        <Text fz={15} fw={400} c={grayColor}>
          {t("dataset").save}
        </Text>
      </>
    );
  };

  const renderFavoritesMenuItem = () => {
    const iconColor = isInMyBookmarks ? euFlagColor : undefined;
    const iconFill = isInMyBookmarks ? euFlagColor : "none";
    const itemStyle = {
      color: iconColor,
      fontWeight: isInMyBookmarks ? 600 : undefined,
    };

    return (
      <Menu.Item
        onClick={() =>
          isInMyBookmarks ? onRemoveFromMyBookmarks() : onSaveToMyBookmarks()
        }
        leftSection={
          <IconFolderHeart size={16} color={iconColor} fill={iconFill} />
        }
        style={itemStyle}
      >
        {t("dataset").favorites}
      </Menu.Item>
    );
  };

  const renderBookmarkSetMenuItem = (bookmarkSet: BookmarkSet) => {
    const isInBookmarkSet =
      bookmarkSet.bookmark_set_bookmark_list?.some(
        (bsb) => bsb.db_table_id === dataset.db_table_id
      ) ?? false;
    const iconColor = isInBookmarkSet ? euFlagColor : undefined;
    const iconFill = isInBookmarkSet ? euFlagColor : "none";
    const itemStyle = {
      color: iconColor,
      fontWeight: isInBookmarkSet ? 600 : undefined,
    };

    return (
      <Menu.Item
        key={bookmarkSet.bookmark_set_id}
        onClick={() =>
          isInBookmarkSet
            ? onRemoveFromBookmarkSet(bookmarkSet.bookmark_set_id)
            : onSaveToBookmarkSet(bookmarkSet.bookmark_set_id)
        }
        leftSection={<IconFolder size={16} color={iconColor} fill={iconFill} />}
        style={itemStyle}
      >
        {bookmarkSet.bookmark_set_name}
      </Menu.Item>
    );
  };

  return (
    <Card p="md" key={dataset.db_table_id} className="dataset-card">
      <Group gap={4} justify="space-between" align="center">
        <Link
          to={`/discover/dataset/${dataset.db_table_id}`}
          className="dataset-card-link"
        >
          <Text className="dataset-card-title" fz={20} c="eu_flag.9" fw={400}>
            {datasetName}
          </Text>
        </Link>
      </Group>
      <Group gap={8} align="center" my={4}>
        <Image
          src={datasetFlagSrc}
          width={14}
          height={10}
          style={flagImageStyle}
        />
        <a
          className="dataset-card-source"
          href={`https://${datasetSource}`}
          target="_blank"
        >
          <Text fz={15} c="gray.9" fw={400}>
            {datasetSource}
          </Text>
        </a>
      </Group>
      <Text fz={16} c="dark.8" lineClamp={2} my={2} lh={1.8}>
        {datasetDescription}
      </Text>
      <Flex direction="row" gap="md" align="center" justify="flex-start" mt={4}>
        <Menu position="bottom-start">
          <Menu.Target>
            <Group gap={6} align="center" className="dataset-card-bookmark">
              {renderBookmarkButton()}
            </Group>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Label>{t("dataset").saveDataset}</Menu.Label>
            {renderFavoritesMenuItem()}
            {bookmarkSets.map(renderBookmarkSetMenuItem)}
            <Menu.Divider />
            <Menu.Item
              disabled
              variant="filled"
              onClick={onCreateNewBookmarkSet}
              leftSection={<IconPlus size={16} />}
            >
              {t("dataset").createNewFolder}
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
        <Divider orientation="vertical" color="gray.3" h={16} my="auto" />
        <Text fz={15} c="gray.9" fw={400}>
          {datasetRowCount} {t("dataset").rows} · {datasetColumnCount}{" "}
          {t("dataset").cols}
        </Text>
        <Divider orientation="vertical" color="gray.3" h={16} my="auto" />
        <Text fz={15} c="gray.9" fw={400}>
          {datasetSize}
        </Text>
        <Divider orientation="vertical" color="gray.3" h={16} my="auto" />
        <Text fz={15} c="gray.9" fw={400}>
          {datasetDateCreated}
        </Text>
      </Flex>
      <Modal
        opened={createModalOpened}
        onClose={handleCloseCreateModal}
        title={t("dataset").createNewBookmarkSet}
        centered
        size="sm"
      >
        <form
          onSubmit={createBookmarkSetForm.onSubmit(handleCreateBookmarkSet)}
        >
          <Flex direction="column" gap="md">
            <TextInput
              placeholder={t("dataset").enterBookmarkSetName}
              {...createBookmarkSetForm.getInputProps("bookmark_set_name")}
              autoFocus
            />
            <Flex justify="flex-end" gap="sm">
              <Button
                variant="outline"
                onClick={handleCloseCreateModal}
                disabled={loading}
              >
                {t("common").cancel}
              </Button>
              <Button type="submit" loading={loading}>
                {t("dataset").create}
              </Button>
            </Flex>
          </Flex>
        </form>
      </Modal>
    </Card>
  );
}
